import useAxios from "src/core/shared/hooks/useAxios";
import {
  PaginatedParams,
  FilterParams
} from "src/core/shared/api";
import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";
import { StringResponse } from "src/modules/support/api/support.api";
import { PaginatedData } from "src/library/utils/apiTypes";
import { ICustomer } from "src/core/customers/api/customers.api";
import { IDrop } from "src/core/drops/api/drops.api";
import {
  CustomerBranchFilterDefault,
  CustomerBranchFilterType
} from "src/core/products/api/products.api";

export type ListOpenDaysParams = { pagination: PaginatedParams<IOpenDay>; filters: FilterParams<IOpenDay> };
export type UpdateOpenDayParams = {openDayId: number, openDayUpdate: Partial<IOpenDay>};

type OpenDayEndpointConfig = {
  LIST_OPEN_DAYS: EndpointConfig<
  ListOpenDaysParams,
    PaginatedData<IOpenDay>
  >;
  GET_OPEN_DAY: EndpointConfig<
    number,
    IOpenDay
  >;
  DELETE_OPEN_DAY: EndpointConfig<
    number,
    StringResponse
  >;
  CREATE_OPEN_DAY: EndpointConfig<
    Omit<IOpenDay, "id">,
    IOpenDay
  >;
  UPDATE_OPEN_DAY: EndpointConfig<
  UpdateOpenDayParams,
    IOpenDay
  >;
};

const requests: RequestConfig<OpenDayEndpointConfig> = {
  LIST_OPEN_DAYS: params => ({
    url: `${process.env.REACT_APP_API_URL}/shutdown/open-days/all`,
    method: "GET",
    params
  }),
  GET_OPEN_DAY: openDayId => ({
    url: `${process.env.REACT_APP_API_URL}/shutdown/open-days/${openDayId}`,
    method: "GET"
  }),
  DELETE_OPEN_DAY: openDayId => ({
    url: `${process.env.REACT_APP_API_URL}/shutdown/open-days/${openDayId}`,
    method: "DELETE"
  }),
  CREATE_OPEN_DAY: openDay => ({
    url: `${process.env.REACT_APP_API_URL}/shutdown/open-days/create`,
    data: openDay,
    method: "POST"
  }),
  UPDATE_OPEN_DAY: ({ openDayId, openDayUpdate }) => ({
    url: `${process.env.REACT_APP_API_URL}/shutdown/open-days/${openDayId}`,
    data: openDayUpdate,
    method: "PATCH"
  })
};

export const useOpenDaysApi: UseApiRequest<OpenDayEndpointConfig> = name => useAxios(requests[ name ], name);

export interface IOpenDay {
  id: number;
  /** Start of date range to allow orders on */
  startDate: Date;
  /** End of date range to allow orders on */
  endDate: Date;
  /** Reason for the open day */
  reason: string;
  /** Whether by default the open day should be allow all or allow only the added filters be default */
  type: CustomerBranchFilterDefault;
  /** customer/branch filters */
  filters?: IOpenDayFilter[];
}

export interface IOpenDayFilter {
  id: number;
  /** Association id to the open day */
  openDayId: number;
  /** Customer to associate with */
  customerId?: number | null;
  customer?: ICustomer;
  /** Branch to associate with */
  branchId?: number | null;
  branch?: IDrop;
  /** Whether to include this drop to allow them to deliver or exclude them */
  filterType: CustomerBranchFilterType;
}

export type CreateOpenDayFilterAttributes = Omit<IOpenDayFilter, "id" | "openDayId"> & Partial<Pick<IOpenDayFilter, "id" | "openDayId">>;
